import type { AuditFilterOption } from './filters/AuditFilterChip';
import type { AuditDatePresetLabels, AuditDateRangeValue } from './filters/auditDatePresets';

/**
 * EAA-1821: the audit screen's filter state, shared by the panels (which own it
 * and turn it into query params) and the table (whose column headers show which
 * columns are filtered and let the operator filter from the header).
 *
 * It lives in its own module rather than in either consumer because both sides
 * need it and neither should import the other — the table must stay a dumb
 * renderer, and the panels must not depend on how the table draws a header.
 */

/** Columns the backend will sort by; anything else it ignores. */
export type AuditSortField = 'created_at' | 'action' | 'success';

export interface AuditSort {
    field: AuditSortField;
    /** 'desc' is the table's own default — newest first. */
    direction: 'asc' | 'desc';
}

/**
 * One filter state shape for BOTH sources. `credentialMode` is only meaningful
 * for the account source and `targetType` only for the admin source; the panel
 * that does not use a field simply leaves it empty rather than the two sources
 * carrying divergent shapes, which is what made the old pair drift.
 */
export interface AuditFilters {
    /** The free-text box — maps to `q`. */
    search: string;
    /** Multi-select — maps to `action`. */
    action: string[];
    /** '' | 'true' | 'false' — maps to `success`. */
    success: string;
    /** Account source only — maps to `credential_mode`. */
    credentialMode: string[];
    /** Admin source only — maps to `target_type`. */
    targetType: string[];
    /** Per-column search on "Đối tượng" — `username` (account) / `target_id` (admin). */
    target: string;
    /** Per-column search on "Người thao tác" — maps to `actor`. */
    actor: string;
    /** Maps to `from`/`to` once resolved through `resolveDateRange`. */
    date: AuditDateRangeValue;
}

/**
 * EAA-1821: the translated vocabulary every facet surface renders — the filter
 * bar's chips AND the table header's per-column funnels. ONE object, built once
 * in `AuditPanelShell`, because the bar and the header offer the same facets:
 * two independently built sets are how a mode ends up reading as a friendly
 * label in the chip and as a raw code one popover away.
 */
export interface AuditFacetOptions {
    action: AuditFilterOption[];
    credentialMode: AuditFilterOption[];
    targetType: AuditFilterOption[];
    success: AuditFilterOption[];
    /** Copy for the date popover, shared by the "Thời gian" chip and the funnel. */
    dateLabels?: AuditDatePresetLabels;
}

export const EMPTY_AUDIT_FILTERS: AuditFilters = {
    search: '',
    action: [],
    success: '',
    credentialMode: [],
    targetType: [],
    target: '',
    actor: '',
    date: { preset: 'all' },
};

export const DEFAULT_AUDIT_SORT: AuditSort = { field: 'created_at', direction: 'desc' };

/** How many filters are switched on — the "Bộ lọc" chip's badge. */
export function countActiveAuditFilters(filters: AuditFilters): number {
    let count = 0;
    if (filters.search.trim()) count += 1;
    if (filters.action.length) count += 1;
    if (filters.success) count += 1;
    if (filters.credentialMode.length) count += 1;
    if (filters.targetType.length) count += 1;
    if (filters.target.trim()) count += 1;
    if (filters.actor.trim()) count += 1;
    if (filters.date.preset !== 'all') count += 1;
    return count;
}

/** The `ordering` query param the backend whitelists. */
export function auditOrderingParam(sort: AuditSort): string {
    return sort.direction === 'desc' ? `-${sort.field}` : sort.field;
}
